import { create } from 'zustand';

const isNarrow = typeof window !== 'undefined' && window.innerWidth < 480;

export type ContentMode = 'game' | 'related';

interface UiState {
  activeWorkspace: 'game' | 'social' | 'ads';
  contentMode: ContentMode;
  sidebarOpen: boolean;
  sidebarView: 'list' | 'calendar';
  chatOpen: boolean;
  activePage: string | null;
  compactCards: boolean;
  newWordMode: boolean;
  // "Resume where I left off": stage to open once the resumed word loads.
  // Set by the binder's Last-worked-on chip, consumed once by GameWorkspace.
  pendingResumeStage: string | null;
  // Word open on the Content tab (D1). Set by the Word Binder or the Content list.
  contentOpenId: string | null;
  // Stage to show when the word opens (a Related Word opens its Game Word at Stage 4).
  contentStage: string | null;
  openContentWord: (id: string | null, stage?: string | null) => void;
  setWorkspace: (workspace: 'game' | 'social' | 'ads') => void;
  setContentMode: (mode: ContentMode) => void;
  setPage: (page: string | null) => void;
  toggleSidebar: () => void;
  setSidebarView: (view: 'list' | 'calendar') => void;
  toggleChat: () => void;
  setChat: (open: boolean) => void;
  toggleCompactCards: () => void;
  setNewWordMode: (on: boolean) => void;
  setPendingResumeStage: (stage: string | null) => void;
}

export const useUiStore = create<UiState>()((set) => ({
  // Active workspace tab
  activeWorkspace: 'game',

  // Which content table the binder + workspace are showing.
  contentMode: 'game',

  // Sidebar state
  sidebarOpen: true,
  sidebarView: 'list',

  // Chat drawer
  chatOpen: false,

  // Page overlay (settings, knowledge editor, etc.)
  activePage: null,

  // Compact card mode
  compactCards: isNarrow,

  // New-word entry form in main workspace
  newWordMode: false,

  // Resume-where-I-left-off stage handoff (see utils/lastWorked.ts)
  pendingResumeStage: null,

  contentOpenId: null,
  contentStage: null,

  // Actions
  setWorkspace: (workspace: 'game' | 'social' | 'ads') => set({ activeWorkspace: workspace, activePage: null, newWordMode: false }),
  setContentMode: (mode: ContentMode) => set({ contentMode: mode, newWordMode: false }),
  setPage: (page: string | null) => set({ activePage: page }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarView: (view: 'list' | 'calendar') => set({ sidebarView: view }),
  toggleChat: () => set((s) => ({ chatOpen: !s.chatOpen })),
  setChat: (open: boolean) => set({ chatOpen: open }),
  toggleCompactCards: () => set((s) => ({ compactCards: !s.compactCards })),
  setNewWordMode: (on: boolean) => set({ newWordMode: on }),
  setPendingResumeStage: (stage: string | null) => set({ pendingResumeStage: stage }),
  openContentWord: (id: string | null, stage: string | null = null) => set(id
    ? { contentOpenId: id, contentStage: stage, activeWorkspace: 'game', activePage: null, sidebarView: 'list', contentMode: 'game', newWordMode: false }
    : { contentOpenId: null, contentStage: null }),
}));
